import React, { useState } from 'react';

import axios from 'axios';
import { Card } from '../../design-system';
import { useEffectOnce, useToggle } from 'react-use';
import ModalDelete from '../../components/ModalDelete';
import ModalForm from '../../components/ModalForm';
import { Table, TableCardHeader } from '../../components/Table';
import { ACTIONS } from '../../contexts/sessionContext';
import { ERR_MSG, SUCCESS_MSG } from '../../globalConstants';
import Alert from '../../helpers/alert';
import { useData } from '../../helpers/data-loader';
import {
  catchPlanDetailError,
  createSelectOptions,
  errorMsg,
  required,
  sortByLabel,
} from '../../helpers/utils';
import { ADD_MEMBER_MODAL_FIELDS, ORG_MEMBERS_COLUMNS } from './constants';

const filterOrgStaff = (data: any[], entityId: any) =>
  data.filter(({ id }) => id !== entityId);

interface UserMGMTProps {
  user: any;
  dispatch: (action: any) => void;
}

const UserMGMT = ({ user, dispatch }: UserMGMTProps) => {
  const [targetedEntityId, setTargetedEntityId] = useState<any>(null);
  const [selectedMember, setMember] = useState<any>();
  const [route, setRoute] = useState<string | null>(null);
  const [isAddModalOpen, toggleAddModal] = useToggle(false);
  const [isDeleteModalOpen, toggleDeleteModal] = useToggle(false);
  const [isEditModalOpen, toggleEditModal] = useToggle(false);
  const members = [...(user.members || []), ...(user.invites || [])];
  const [clients, fetchClients] = useData('/client', {
    errorMsg: errorMsg('clients'),
  });
  const clientChoices = createSelectOptions(clients).sort(sortByLabel);
  const modalActions = {
    delete: toggleDeleteModal,
    edit: toggleEditModal,
  };

  const prepModal = (action: string, id: any, newRoute: string) => {
    const newMember = members.find((member) => member.id === id);
    setMember({ ...newMember, setPassword: 'send' });
    setTargetedEntityId(id);
    setRoute(newRoute);
    modalActions[action](true);
  };
  const destroyModal = () => {
    setTargetedEntityId(null);
    setRoute(null);
    toggleDeleteModal(false);
  };

  const handleAddUser = async (values: any) => {
    const { isClient } = values;

    if (!user.agency?.id) {
      Alert.error('No agency found for this user.');
      return;
    }

    try {
      const { data } = await axios.post('/invite', {
        ...values,
        isClient,
        agency_id: user.agency.id,
      });
      Alert.success('Successfully sent invite');
      dispatch({
        type: ACTIONS.SET_USER,
        value: { ...user, invites: [...(user.invites || []), data] },
      });
      toggleAddModal(false);
    } catch (error: any) {
      Alert.error(ERR_MSG);
    }
  };

  const handleUpdateUser = async (values: any) => {
    const { isClient } = values;
    try {
      const { data } = await axios.put(`/user/${selectedMember.id}`, {
        ...values,
        isClient,
      });
      Alert.success(SUCCESS_MSG);
      dispatch({
        type: ACTIONS.SET_USER,
        value: {
          ...user,
          members:
            data.members ||
            user.members.map((m) =>
              m.id === selectedMember.id ? { ...m, ...data } : m,
            ),
        },
      });
      toggleEditModal(false);
    } catch (error) {
      catchPlanDetailError(error?.response);
    }
  };

  const handleDeleteUser = async () => {
    const isOrgUser = route === '/user';
    try {
      await axios.delete(`${route}/${targetedEntityId}`);
      dispatch({
        type: ACTIONS.SET_USER,
        value: {
          ...user,
          members: isOrgUser
            ? filterOrgStaff(user.members || [], targetedEntityId)
            : user.members,
          invites: !isOrgUser
            ? filterOrgStaff(user.invites || [], targetedEntityId)
            : user.invites,
        },
      });
      destroyModal();
      Alert.success(SUCCESS_MSG);
    } catch (error: any) {
      Alert.error(ERR_MSG);
    }
  };

  const extraFields = [
    {
      name: 'role_id',
      label: 'Role',
      component: 'select',
      options:
        // values start at 3 to correspond
        // to the equivalent role.id in DB
        user.agency && user.agency.plan_id !== 1
          ? [
              {
                value: 3,
                label: 'Editor',
              },
              {
                value: 4,
                label: 'Viewer',
              },
            ]
          : [{ value: 4, label: 'Viewer' }],
      validate: required,
    },
    {
      name: 'isClient',
      label: 'External User',
      component: 'checkbox',
    },
    {
      name: 'clients',
      label: 'Clients',
      component: 'checkbox-group',
      choices: clientChoices,
    },
  ];

  useEffectOnce(() => {
    fetchClients();
  });

  return (
    <Card minHeight="48rem" display="flex" flexDirection="column">
      <TableCardHeader
        name={`Members of ${user.agency?.name ?? ''}`}
        action="Add Member"
        onClick={() => toggleAddModal(true)}
      />
      <Table
        name="Members"
        columns={ORG_MEMBERS_COLUMNS}
        data={members.map((item) => ({
          ...item,
          prepModal,
          user,
        }))}
      />
      <ModalForm
        title="Add New User"
        fields={[...ADD_MEMBER_MODAL_FIELDS, ...extraFields]}
        isOpen={isAddModalOpen}
        onClose={() => toggleAddModal(false)}
        onSubmit={handleAddUser}
        initialValues={{ setPassword: 'send' }}
        oneColumn
      />
      <ModalDelete
        entity="user"
        open={isDeleteModalOpen}
        onCancel={destroyModal}
        onDelete={handleDeleteUser}
      />
      <ModalForm
        title="Edit User"
        isOpen={isEditModalOpen}
        oneColumn
        onClose={() => toggleEditModal(false)}
        onSubmit={handleUpdateUser}
        fields={[...ADD_MEMBER_MODAL_FIELDS, ...extraFields]}
        initialValues={selectedMember}
      />
    </Card>
  );
};

export default UserMGMT;
